'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { twMerge } from 'tailwind-merge'

import type { TimeSeriesPoint } from '@/types/usaspending'

import { Currency } from '../Formatters'

export interface SpendingTrendChartProps {
  data: TimeSeriesPoint[]
  height?: number
  accentColor?: 'blue' | 'indigo' | 'purple' | 'teal' | 'amber'
  showGrid?: boolean
  showTooltip?: boolean
  className?: string
}

/** Month abbreviations for X-axis labels */
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/** Full month names for tooltips */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * Format a month/year for X-axis label: "Jan '24"
 */
function formatMonthLabel(year: number, month: number): string {
  return `${MONTH_ABBR[month - 1]} '${String(year).slice(-2)}`
}

/**
 * Format a month/year for tooltip: "January 2024"
 */
function formatMonthTooltip(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1]} ${year}`
}

const ACCENT_COLORS = {
  blue: {
    stroke: '#3b82f6',
    fill: 'rgba(59, 130, 246, 0.15)',
    dark: { stroke: '#60a5fa', fill: 'rgba(96, 165, 250, 0.2)' },
  },
  indigo: {
    stroke: '#6366f1',
    fill: 'rgba(99, 102, 241, 0.15)',
    dark: { stroke: '#818cf8', fill: 'rgba(129, 140, 248, 0.2)' },
  },
  purple: {
    stroke: '#a855f7',
    fill: 'rgba(168, 85, 247, 0.15)',
    dark: { stroke: '#c084fc', fill: 'rgba(192, 132, 252, 0.2)' },
  },
  teal: {
    stroke: '#14b8a6',
    fill: 'rgba(20, 184, 166, 0.15)',
    dark: { stroke: '#2dd4bf', fill: 'rgba(45, 212, 191, 0.2)' },
  },
  amber: {
    stroke: '#f59e0b',
    fill: 'rgba(245, 158, 11, 0.15)',
    dark: { stroke: '#fbbf24', fill: 'rgba(251, 191, 36, 0.2)' },
  },
}

// Red colors for negative values (de-obligations)
const NEGATIVE_COLORS = {
  stroke: '#ef4444',
  fill: 'rgba(239, 68, 68, 0.15)',
  dark: { stroke: '#f87171', fill: 'rgba(248, 113, 113, 0.2)' },
}

/**
 * SpendingTrendChart
 *
 * Area chart with gradient fill for spending trends.
 * Supports negative values (de-obligations) shown in red below zero line.
 *
 * Features:
 * - Smooth curved line with area fill
 * - Blue fill above zero, red fill below zero
 * - Animated reveal on mount
 * - Hover tooltip with exact values
 * - Responsive sizing
 */
export const SpendingTrendChart: React.FC<SpendingTrendChartProps> = ({
  data,
  height = 160,
  accentColor = 'blue',
  showGrid = true,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 600, height })
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0,
    screenX: 0,
    screenY: 0,
  })
  const [isDark, setIsDark] = useState(false)

  const colors = ACCENT_COLORS[accentColor]
  const negColors = NEGATIVE_COLORS

  // Detect dark mode
  useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'))
    }
    checkDark()
    const observer = new MutationObserver(checkDark)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })
    return () => observer.disconnect()
  }, [])

  // Resize observer for responsive width
  useEffect(() => {
    if (!containerRef.current) return
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({ width: entry.contentRect.width, height })
      }
    })
    resizeObserver.observe(containerRef.current)
    return () => resizeObserver.disconnect()
  }, [height])

  // Track mobile for responsive adjustments
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Chart padding - reduced on mobile
  const padding = {
    top: 16,
    right: isMobile ? 8 : 16,
    bottom: 28,
    left: isMobile ? 36 : 48,
  }
  const chartWidth = dimensions.width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  // Compute scale and points (supports negative values)
  const {
    minValue,
    maxValue,
    zeroY,
    points,
    yTicks,
    labelIndices,
    yearBoundaryIndices,
  } = useMemo(() => {
    if (data.length === 0) {
      return {
        minValue: 0,
        maxValue: 100,
        zeroY: padding.top + chartHeight,
        points: [],
        yTicks: [],
        labelIndices: new Set<number>(),
        hasNegative: false,
      }
    }

    const amounts = data.map((d) => d.amount)
    const rawMin = Math.min(...amounts)
    const rawMax = Math.max(...amounts)

    // Add padding to the range
    const min = rawMin < 0 ? rawMin * 1.1 : 0
    const max = rawMax > 0 ? rawMax * 1.1 : 0
    const range = max - min || 100

    // Calculate zero line position
    const zeroYPos =
      padding.top + chartHeight - ((0 - min) / range) * chartHeight

    // Generate Y-axis ticks - show min, 0, and max
    // Always show $0 when there are negatives (it's the red/blue boundary)
    const ticks: number[] = []

    if (min < 0 && max > 0) {
      // Has both positive and negative - always show min, 0, and max
      // Add one intermediate positive tick if there's room
      ticks.push(min, 0, max * 0.5, max)
    } else if (min < 0) {
      // Only negative
      ticks.push(min, min * 0.5, 0)
    } else {
      // Only positive
      ticks.push(0, max * 0.33, max * 0.67, max)
    }

    // Calculate points
    const pts = data.map((d, i) => ({
      x:
        padding.left +
        (data.length > 1
          ? (i / (data.length - 1)) * chartWidth
          : chartWidth / 2),
      y: padding.top + chartHeight - ((d.amount - min) / range) * chartHeight,
      year: d.year,
      month: d.month,
      amount: d.amount,
      transactionCount: d.transactionCount,
    }))

    // Calculate which labels to show
    const indices = new Set<number>()

    // Find all January indices (year boundaries) for vertical lines
    const januaryIndices: number[] = []
    for (let i = 0; i < data.length; i++) {
      if (data[i].month === 1) {
        januaryIndices.push(i)
      }
    }

    // Always show first and last
    indices.add(0)
    if (data.length > 1) {
      indices.add(data.length - 1)
    }

    if (isMobile) {
      // Mobile: add January labels if they're not too close to first/last
      // Skip if within 2 months of start or end to avoid overlap
      januaryIndices.forEach((idx) => {
        if (idx > 2 && idx < data.length - 3) {
          indices.add(idx)
        }
      })
    } else {
      // Desktop: show Jan, Apr, Jul, Oct
      for (let i = 0; i < data.length; i++) {
        const month = data[i].month
        if (month === 1 || month === 4 || month === 7 || month === 10) {
          indices.add(i)
        }
      }
    }

    return {
      minValue: min,
      maxValue: max,
      zeroY: zeroYPos,
      points: pts,
      yTicks: ticks,
      labelIndices: indices,
      yearBoundaryIndices: januaryIndices,
      hasNegative: rawMin < 0,
    }
  }, [
    data,
    chartWidth,
    chartHeight,
    padding.left,
    padding.right,
    padding.top,
    padding.bottom,
    isMobile,
  ])

  // Generate line path using simple line segments (more reliable than curves for many points)
  const linePath = useMemo(() => {
    if (points.length === 0) return ''
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`

    // Use polyline for reliability with many data points
    return points
      .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(' ')
  }, [points])

  // Generate area paths for positive and negative regions
  // Uses clip paths to properly separate above/below zero
  const { positiveAreaPath, negativeAreaPath } = useMemo(() => {
    if (points.length === 0)
      return { positiveAreaPath: '', negativeAreaPath: '' }

    // Build the complete line path with segments
    const segments: Array<{ x: number; y: number }> = []

    for (let i = 0; i < points.length; i++) {
      const p = points[i]

      if (i === 0) {
        segments.push({ x: p.x, y: p.y })
      } else {
        const prevP = points[i - 1]
        const prevAboveZero = prevP.y <= zeroY
        const currAboveZero = p.y <= zeroY

        // If crossing zero line, add intersection point
        if (prevAboveZero !== currAboveZero) {
          const t = (zeroY - prevP.y) / (p.y - prevP.y)
          const crossX = prevP.x + t * (p.x - prevP.x)
          segments.push({ x: crossX, y: zeroY })
        }

        segments.push({ x: p.x, y: p.y })
      }
    }

    // Build positive area (above zero, y <= zeroY)
    const posPath: string[] = []
    let posStartX: number | null = null

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      const isAbove = seg.y <= zeroY

      if (isAbove) {
        if (posStartX === null) {
          posStartX = seg.x
          posPath.push(`M ${seg.x} ${zeroY} L ${seg.x} ${seg.y}`)
        } else {
          posPath.push(`L ${seg.x} ${seg.y}`)
        }
      } else if (posStartX !== null) {
        // Just crossed below zero, close the positive shape
        posPath.push(`L ${segments[i - 1].x} ${zeroY} Z`)
        posStartX = null
      }
    }
    // Close if ended above zero
    if (posStartX !== null && posPath.length > 0) {
      posPath.push(`L ${segments[segments.length - 1].x} ${zeroY} Z`)
    }

    // Build negative area (below zero, y > zeroY)
    const negPath: string[] = []
    let negStartX: number | null = null

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      const isBelow = seg.y > zeroY

      if (isBelow) {
        if (negStartX === null) {
          negStartX = seg.x
          negPath.push(`M ${seg.x} ${zeroY} L ${seg.x} ${seg.y}`)
        } else {
          negPath.push(`L ${seg.x} ${seg.y}`)
        }
      } else if (negStartX !== null) {
        // Just crossed above zero, close the negative shape
        negPath.push(`L ${segments[i - 1].x} ${zeroY} Z`)
        negStartX = null
      }
    }
    // Close if ended below zero
    if (negStartX !== null && negPath.length > 0) {
      negPath.push(`L ${segments[segments.length - 1].x} ${zeroY} Z`)
    }

    return {
      positiveAreaPath: posPath.join(' '),
      negativeAreaPath: negPath.join(' '),
    }
  }, [points, zeroY])

  // Format currency for Y-axis
  const formatYAxis = (value: number) => {
    const absValue = Math.abs(value)
    const sign = value < 0 ? '-' : ''
    if (absValue >= 1e12) return `${sign}$${(absValue / 1e12).toFixed(1)}T`
    if (absValue >= 1e9) return `${sign}$${(absValue / 1e9).toFixed(1)}B`
    if (absValue >= 1e6) return `${sign}$${(absValue / 1e6).toFixed(1)}M`
    if (absValue >= 1e3) return `${sign}$${(absValue / 1e3).toFixed(0)}K`
    return `${sign}$${absValue.toFixed(0)}`
  }

  // Handle mouse move over chart area - find nearest point
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (data.length === 0) return

      const container = event.currentTarget
      const rect = container.getBoundingClientRect()
      const mouseX = event.clientX - rect.left

      const actualChartWidth = rect.width - padding.left - padding.right

      // Find nearest point by X coordinate
      let nearestIndex = 0
      let minDistance = Infinity

      for (let i = 0; i < data.length; i++) {
        const pointX =
          padding.left +
          (data.length > 1
            ? (i / (data.length - 1)) * actualChartWidth
            : actualChartWidth / 2)
        const distance = Math.abs(pointX - mouseX)
        if (distance < minDistance) {
          minDistance = distance
          nearestIndex = i
        }
      }

      // Only show hover if within reasonable distance
      if (minDistance < 40 && points[nearestIndex]) {
        const point = points[nearestIndex]
        const screenX = rect.left + point.x
        const screenY = rect.top + point.y

        setHoveredIndex(nearestIndex)
        setTooltipPos({ x: point.x, y: point.y, screenX, screenY })
      } else {
        setHoveredIndex(null)
      }
    },
    [data, points, padding.left, padding.right],
  )

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null)
  }, [])

  if (data.length === 0) {
    return (
      <div
        className={twMerge(
          'flex items-center justify-center rounded-lg border border-dashed border-primary-300 dark:border-primary-700',
          className,
        )}
        style={{ height }}
      >
        <span className="text-sm text-primary-400 dark:text-primary-500">
          No data available
        </span>
      </div>
    )
  }

  const activeColors = isDark ? colors.dark : colors
  const activeNegColors = isDark ? negColors.dark : negColors
  const range = maxValue - minValue || 100

  return (
    <div
      ref={containerRef}
      className={twMerge('relative w-full overflow-visible', className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${dimensions.width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        className="overflow-visible"
      >
        {/* Gradient definitions */}
        <defs>
          <linearGradient
            id={`trend-gradient-pos-${accentColor}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor={activeColors.stroke}
              stopOpacity={0.3}
            />
            <stop
              offset="100%"
              stopColor={activeColors.stroke}
              stopOpacity={0.02}
            />
          </linearGradient>
          <linearGradient
            id={`trend-gradient-neg-${accentColor}`}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="0%"
              stopColor={activeNegColors.stroke}
              stopOpacity={0.02}
            />
            <stop
              offset="100%"
              stopColor={activeNegColors.stroke}
              stopOpacity={0.3}
            />
          </linearGradient>
        </defs>

        {/* Horizontal grid lines */}
        {showGrid && (
          <g>
            {yTicks.map((tick, i) => {
              const y =
                padding.top +
                chartHeight -
                ((tick - minValue) / range) * chartHeight
              return (
                <line
                  key={i}
                  x1={padding.left}
                  x2={dimensions.width - padding.right}
                  y1={y}
                  y2={y}
                  stroke="currentColor"
                  strokeWidth={Math.abs(tick) < 0.01 ? 1.5 : 1}
                  strokeDasharray={Math.abs(tick) < 0.01 ? undefined : '3 3'}
                  className={twMerge(
                    'text-primary-200 dark:text-primary-700',
                    Math.abs(tick) < 0.01 ? 'opacity-100' : 'opacity-60',
                  )}
                />
              )
            })}
          </g>
        )}

        {/* Year boundary vertical lines with year labels */}
        {showGrid && yearBoundaryIndices && yearBoundaryIndices.length > 0 && (
          <g>
            {yearBoundaryIndices.map((idx) => {
              const point = points[idx]
              if (!point) return null
              const year = data[idx].year
              return (
                <g key={`year-${idx}`}>
                  <line
                    x1={point.x}
                    x2={point.x}
                    y1={padding.top}
                    y2={padding.top + chartHeight}
                    stroke="currentColor"
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    className="text-primary-300 dark:text-primary-600 opacity-70"
                  />
                  {/* Year label at top of line */}
                  <text
                    x={point.x + 4}
                    y={padding.top + 10}
                    className="text-[9px] font-medium fill-primary-400 dark:fill-primary-500"
                  >
                    {`'${String(year).slice(-2)}`}
                  </text>
                </g>
              )
            })}
          </g>
        )}

        {/* Positive area fill (blue) */}
        {positiveAreaPath && (
          <path
            d={positiveAreaPath}
            fill={`url(#trend-gradient-pos-${accentColor})`}
            className="animate-[fadeIn_500ms_ease-out]"
          />
        )}

        {/* Negative area fill (red) */}
        {negativeAreaPath && (
          <path
            d={negativeAreaPath}
            fill={`url(#trend-gradient-neg-${accentColor})`}
            className="animate-[fadeIn_500ms_ease-out]"
          />
        )}

        {/* Main line */}
        <path
          d={linePath}
          fill="none"
          stroke={activeColors.stroke}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((point, i) => {
          const isNegative = data[i].amount < 0
          const pointColor = isNegative
            ? activeNegColors.stroke
            : activeColors.stroke
          // Smaller points on mobile
          const baseRadius = isMobile ? 2 : 3
          const hoverRadius = isMobile ? 4 : 5
          return (
            <circle
              key={i}
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === i ? hoverRadius : baseRadius}
              fill={isDark ? '#1f2937' : '#fff'}
              stroke={pointColor}
              strokeWidth={isMobile ? 1.5 : 2}
              className="transition-all duration-150"
            />
          )
        })}

        {/* Y-axis labels - filter out overlapping labels, prioritize negative over $0 */}
        <g className="text-primary-500 dark:text-primary-400">
          {(() => {
            const minLabelSpacing = 14 // minimum pixels between labels
            const labels: Array<{ tick: number; y: number }> = yTicks.map(
              (tick) => ({
                tick,
                y:
                  padding.top +
                  chartHeight -
                  ((tick - minValue) / range) * chartHeight,
              }),
            )

            // Filter out labels that are too close, prioritizing negative over $0
            const filtered: typeof labels = []
            // Sort: negative first, then non-zero, then $0 last (so $0 gets dropped if overlap)
            const sorted = [...labels].sort((a, b) => {
              if (a.tick < 0) return -1
              if (b.tick < 0) return 1
              if (a.tick === 0) return 1
              if (b.tick === 0) return -1
              return 0
            })

            for (const label of sorted) {
              const tooClose = filtered.some(
                (f) => Math.abs(f.y - label.y) < minLabelSpacing,
              )
              if (!tooClose) {
                filtered.push(label)
              }
            }

            return filtered.map(({ tick, y }, i) => (
              <text
                key={i}
                x={padding.left - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className={twMerge(
                  'text-[10px] font-medium tabular-nums',
                  tick < 0
                    ? 'fill-red-500 dark:fill-red-400'
                    : 'fill-primary-500 dark:fill-primary-400',
                )}
              >
                {formatYAxis(tick)}
              </text>
            ))
          })()}
        </g>

        {/* X-axis labels - evenly distributed for readability */}
        <g className="text-primary-500 dark:text-primary-400">
          {points.map((point, i) => {
            if (!labelIndices.has(i)) return null

            return (
              <text
                key={i}
                x={point.x}
                y={height - 6}
                textAnchor="middle"
                className="text-[10px] font-medium tabular-nums fill-primary-500 dark:fill-primary-400"
              >
                {formatMonthLabel(point.year, point.month)}
              </text>
            )
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {hoveredIndex !== null && data[hoveredIndex] && (
        <div
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full opacity-0 animate-[fadeIn_150ms_ease-out_forwards]"
          style={{
            left: tooltipPos.screenX,
            top: tooltipPos.screenY - 8,
          }}
        >
          <div className="rounded-lg border border-primary-200 bg-white px-3 py-2 shadow-lg dark:border-primary-700 dark:bg-primary-900">
            <div className="mb-0.5 text-xs font-semibold text-primary-900 dark:text-primary-100">
              {formatMonthTooltip(
                data[hoveredIndex].year,
                data[hoveredIndex].month,
              )}
            </div>
            <div
              className="text-sm font-bold tabular-nums"
              style={{
                color:
                  data[hoveredIndex].amount < 0
                    ? activeNegColors.stroke
                    : activeColors.stroke,
              }}
            >
              <Currency value={data[hoveredIndex].amount} />
            </div>
            <div className="text-[10px] text-primary-500 dark:text-primary-400">
              {data[hoveredIndex].transactionCount.toLocaleString()}{' '}
              transactions
            </div>
          </div>
          <div className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-r border-primary-200 bg-white dark:border-primary-700 dark:bg-primary-900" />
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}
